import React, {useContext, useMemo} from 'react';
import {AttentionMessageProps} from './index';
import {UserContext} from '../../../../../src/contexts/UserContext';
import OrderAttentionControls, {makeOrderAttentionControlListener} from '../OrderAttentionControls';
import Translate from '../../../../../src/components/Translate/Translate';
import {FormDataProviderAPIContext} from '../../../../../src/contexts/FormDataProviderAPIContext';
import {MessageComponentType} from './ControlledOrderAttentionMessage';
import {IOrderAttentionModel} from '../../../../../src/@interfaces/models/Order/IOrderAttentionModel';
import {useQueryClient} from '@tanstack/react-query';
import app from '../../../../../src/helpers/app';

function OrderStatusExpiredMessage(props: MessageComponentType) {
    const context = useContext(FormDataProviderAPIContext);
    const model: IOrderAttentionModel<'order-status-expired'> = props.model as any;
    const details = model.details;
    if (!context || !details) {
        return null;
    }

    const {modelOptions} = context;
    const statusName = modelOptions?.status?.find(({value}) => value === details.status_id)?.label;

    return <Translate
            message={props.message}
            replace={{
                status: statusName ?? details.status_id,
                number: details.renewal_number ?? 0,
            }}
    />;
}

OrderStatusExpired.getMessageComponent = () => OrderStatusExpiredMessage;

export default function OrderStatusExpired({catchHttpException, ...props}: AttentionMessageProps) {
    const userContext = useContext(UserContext);
    const queryClient = useQueryClient();

    const modelId = props.model.id;
    const onAccept = useMemo(() => {
        const make = <Key extends 'onAccept' | 'onReject'>(routeName: string) => {
            return makeOrderAttentionControlListener<Key>(
                    routeName,
                    modelId,
                    () => queryClient.refetchQueries({
                        exact: true,
                        queryKey: app('queryKeys').getCustomQueryOfExactModelQueryKey(
                                'Orders',
                                props.model.order_id,
                                'attentions-list'
                        )
                    }),
                    catchHttpException,
                    undefined,
            );
        };

        return make<'onAccept'>('orders.accept-attention');
    }, [modelId, props.model.order_id, catchHttpException, queryClient]);

    if (!userContext) {
        return null;
    }

    return <OrderAttentionControls
            model={props.model}
            commentFieldPlaceholder="OrderAttentionsField.enter-comment"
            hideAccept={true}
            onReject={onAccept}
            rejectButtonColor="success"
            rejectButtonLabel="OrderAttentionsField.got-it"
    />;
}
